import '../styles/globals.css'
import type {AppProps} from 'next/app'
import MainLayout from '../src/app/layout/MainLayout'
import {useRouter} from "next/router";
import React, {ReactElement, ReactNode, useEffect, useState} from "react";
import Head from "next/head";
import {store} from "@/redux/store";
import {Provider} from "react-redux";
import {NextPage} from "next";
import AccountLayout from "@/app/layout/AccountLayout";
import {getSession, SessionProvider} from "next-auth/react";
import {Session} from "next-auth";
import EmptyLayout from "@/app/layout/EmptyLayout";
import dynamic from "next/dynamic";
import { GoogleTagManager } from '@next/third-parties/google';
import Cookies from "js-cookie";

type NextPageWithLayout = NextPage & {
    getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout
}

function MyApp({Component, pageProps}: AppPropsWithLayout) {
    const router = useRouter();
    const [session, setSession] = useState<Session | null>(null);

    useEffect(() => {
        if (!sessionStorage.getItem('source_t')) {
            const { query } = router;
            const referrer = document.referrer?.toLowerCase() || '';

            const sourcesMap: Record<string, string> = {
                tiktok: 'TIKTOK',
                instagram: 'INSTAGRAM',
                facebook: 'FACEBOOK',
                fb: 'FACEBOOK',
                google: 'GOOGLE',
                subito: 'SUBITO.IT',
                autoscout24: 'AUTOSCOUT.IT',
                rentago: 'RENTAGO',
                messaggero: 'MESSAGGERO',
            };

            const getSource = (value?: string) =>
                value ? Object.keys(sourcesMap).find(key => value.includes(key)) || null : null;

            let source = getSource(query.utm_source?.toString().toLowerCase())
                || (query.gclid ? 'GOOGLE' : null)
                || (query.fbclid ? 'FACEBOOK' : null)
                || getSource(referrer)
                || 'WEB'; // Default a WEB se nessuna fonte trovata

            sessionStorage.setItem('source_t', sourcesMap[source] || 'WEB');
        }

        async function fetchSession() {
            const sessionData = await getSession();
            setSession(sessionData);
        }

        fetchSession();
    }, [router.query]);


    useEffect(() => {
        const resetActivity = () => {
            Cookies.set("lastActivity", Date.now().toString(), { path: "/" });
        };

        // Ascolta eventi di attività dell'utente
        window.addEventListener("mousemove", resetActivity);
        window.addEventListener("keydown", resetActivity);
        window.addEventListener("click", resetActivity);
        window.addEventListener("scroll", resetActivity);

        return () => {
            window.removeEventListener("mousemove", resetActivity);
            window.removeEventListener("keydown", resetActivity);
            window.removeEventListener("click", resetActivity);
            window.removeEventListener("scroll", resetActivity);
        };
    }, []);

    let getLayout;

    if (router.pathname.startsWith('/account')) {
        getLayout = (page: ReactElement) => <AccountLayout>{page}</AccountLayout>;
    } else if (router.pathname.startsWith('/venditore')) {
        getLayout = (page: ReactElement) => <EmptyLayout>{page}</EmptyLayout>;
    } else {
        getLayout = (page: ReactElement) => <MainLayout>{page}</MainLayout>;
    }

    return (
        <SessionProvider session={session}>
            <Provider store={store}>
                <Head>
                    <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1"/>
                    {/* JSON-LD per il WebSite */}
                    <script type="application/ld+json" dangerouslySetInnerHTML={{
                        __html: `
                    {
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        "url": "https://www.gruppopiccirillo.it/",
                        "potentialAction": {
                            "@type": "SearchAction",
                            "target": "https://www.gruppopiccirillo.it/?q={search_term_string}",
                            "query-input": "required name=search_term_string"
                        }
                    }
                    `
                    }}/>

                </Head>
                {/* Google Tag Manager */}
                {getLayout(<Component {...pageProps} />)}
            </Provider>
        </SessionProvider>
    )
}

export default MyApp;
